import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OTHER, findCountries, flagOf, fmtNat, joinPhone, natHint, natOk, phoneInput, splitPhone, type Country } from '@tg/domain';

/* Поле телефона с выбором страны: вместо постоянного «+7» — кнопка «+7 ▾», под полем — список стран
   с поиском (по названию, коду «375» / «+375»), флаг, название и код; стрелки, Enter, Esc.
   Список шириной с поле (ближайший [data-cc-box] или родитель), не влезает вниз — открывается вверх.
   Стили .cc и .ccp — в CSS каждого приложения (тот же вид на лендинге — tools/landing-auth.js). */
export function CountryCode({ c, onChange, className = '' }: { c: Country; onChange: (c: Country) => void; className?: string }) {
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" ref={btn} className={'cc ' + className + (open ? ' open' : '')} aria-haspopup="listbox" aria-expanded={open} aria-label={'Страна: ' + (c.name || 'другая')} onClick={() => setOpen(!open)}>
        {'+' + c.code}
      </button>
      {open && btn.current && (
        <CountryList
          anchor={btn.current}
          cur={c}
          onPick={(x) => {
            setOpen(false);
            onChange(x);
            /* Дальше — набирать номер */
            const inp = (btn.current?.closest('[data-cc-box]') || btn.current?.parentElement)?.querySelector('input');
            setTimeout(() => inp?.focus());
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CountryList({ anchor, cur, onPick, onClose }: { anchor: HTMLElement; cur: Country; onPick: (c: Country) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const list = [...findCountries(q), OTHER];
  const [at, setAt] = useState(() => Math.max(0, list.indexOf(cur)));
  const box = useRef<HTMLDivElement>(null);
  const ul = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const inp = useRef<HTMLInputElement>(null);
  /* Фокус в поиск — когда список уже на месте (скрытому фокус не ставится) */
  const shown = pos.visibility !== 'hidden';
  useEffect(() => {
    if (shown) inp.current?.focus({ preventScroll: true });
  }, [shown]);

  useLayoutEffect(() => {
    const place = () => {
      const f = (anchor.closest('[data-cc-box]') || anchor.parentElement || anchor).getBoundingClientRect();
      const below = innerHeight - f.bottom - 12;
      const up = below < 260 && f.top > below;
      const h = Math.min(360, (up ? f.top : below) - 12);
      setPos(up ? { left: f.left, width: f.width, bottom: innerHeight - f.top + 6, maxHeight: h } : { left: f.left, width: f.width, top: f.bottom + 6, maxHeight: h });
    };
    place();
    const out = (e: Event) => !box.current?.contains(e.target as Node) && !anchor.contains(e.target as Node) && onClose();
    addEventListener('resize', place);
    addEventListener('scroll', place, true);
    document.addEventListener('pointerdown', out, true);
    return () => {
      removeEventListener('resize', place);
      removeEventListener('scroll', place, true);
      document.removeEventListener('pointerdown', out, true);
    };
  }, [anchor, onClose]);
  /* Выбранная — в середине списка при открытии; дальше — следим, чтобы подсвеченная была видна */
  useEffect(() => {
    const r = ul.current?.children[at] as HTMLElement | undefined;
    r?.scrollIntoView({ block: 'nearest' });
  }, [at, q]);
  useEffect(() => {
    const r = ul.current?.children[at] as HTMLElement | undefined;
    if (r && ul.current) ul.current.scrollTop = r.offsetTop - ul.current.clientHeight / 2 + r.offsetHeight / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') setAt((i) => Math.min(list.length - 1, i + 1));
    else if (e.key === 'ArrowUp') setAt((i) => Math.max(0, i - 1));
    else if (e.key === 'Enter') list[at] && onPick(list[at]);
    else if (e.key === 'Escape') {
      onClose();
      anchor.focus();
    } else return;
    e.preventDefault();
    /* Esc закрывает только список, не окно, в котором поле */
    e.stopPropagation();
  };

  return createPortal(
    <div className="ccp" ref={box} style={pos} onKeyDown={key}>
      <label className="ccp-s">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="7" cy="7" r="4.6" />
          <path d="m10.5 10.5 3 3" />
        </svg>
        <input
          ref={inp}
          value={q}
          placeholder="Страна или код"
          aria-label="Поиск страны"
          autoComplete="off"
          onChange={(e) => {
            setQ(e.target.value);
            setAt(0);
          }}
        />
      </label>
      <div className="ccp-l" ref={ul} role="listbox">
        {list.map((x, i) => (
          <button
            type="button"
            key={x.iso || 'other'}
            role="option"
            aria-selected={x === cur}
            className={'ccp-r' + (i === at ? ' ccp-at' : '') + (x === cur ? ' ccp-cur' : '') + (x === OTHER ? ' ccp-oth' : '')}
            onMouseMove={() => i !== at && setAt(i)}
            onClick={() => onPick(x)}
          >
            <span className="ccp-f">{flagOf(x)}</span>
            <span className="ccp-n">{x === OTHER ? 'Другая страна — ввести код' : x.name}</span>
            {x.code && <span className="ccp-c">+{x.code}</span>}
          </button>
        ))}
      </div>
      {list.length === 1 && <div className="ccp-e">Такой страны нет в списке — выберите «Другая страна» и введите код вместе с номером</div>}
    </div>,
    document.body,
  );
}

/** Состояние поля: страна + цифры после кода. init — сохранённый номер в E.164 */
export function usePhone(init?: string | null) {
  const [st, setSt] = useState(() => splitPhone(init));
  const { c, nat } = st;
  return {
    c,
    nat,
    /** Значение поля ввода — с маской */
    value: fmtNat(nat, c),
    placeholder: natHint(c),
    ok: natOk(nat, c),
    /** Номер для сервера (E.164) */
    full: joinPhone(nat, c),
    setCountry: (x: Country) => setSt({ c: x, nat: nat.slice(0, x.n[1]) }),
    onInput: (v: string) => setSt(phoneInput(v, c)),
    reset: (e164?: string | null) => setSt(splitPhone(e164)),
  };
}
